import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import meetlyApi from '../api/meetly';
import { clearActiveSession, type ActiveSessionRecord } from '../hooks/useMeetlyChunkedSession';
import { formatDurationLabel } from '../utils/meetlyAudio';

/**
 * Recovery for a recording whose page went away mid-meeting.
 *
 * A refreshed tab, a crashed renderer, a desktop app quit and reopened — the
 * recorder in the page is gone in all three, but the chunks it already uploaded
 * are on the server and most of them are transcribed. Before chunking there was
 * nothing to recover: the audio lived in a JS array and died with the page.
 *
 * The one thing this must never do is start a second session for the same
 * meeting. It finalizes the session that already exists, or forgets it.
 */

interface Props {
  session: ActiveSessionRecord;
  onResumed: (meetingId: string) => void;
  onDiscarded: () => void;
}

export function MeetlyInterruptedSession({ session, onResumed, onDiscarded }: Props) {
  const [busy, setBusy] = useState<'finish' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const elapsed = Math.max(0, Math.round((Date.now() - session.startedAt) / 1000));

  const finish = async () => {
    setBusy('finish');
    setError(null);
    try {
      // No total_chunks: the count this page knew died with it, and claiming one
      // the server cannot satisfy would make it refuse as "chunks missing".
      await meetlyApi.finalizeSession(session.sessionId);
      clearActiveSession();
      onResumed(session.meetingId);
    } catch {
      setError('That recording could not be finished. It may already have been processed.');
      setBusy(null);
    }
  };

  const discard = () => {
    setBusy('discard');
    // Nothing is deleted server-side: an unfinalized session is never summarised
    // and never becomes a meeting, so forgetting it here is enough.
    clearActiveSession();
    onDiscarded();
  };

  return (
    <section
      className="gt-card mx-auto max-w-2xl overflow-hidden"
      style={{ borderColor: 'rgba(217,119,6,0.28)' }}
      aria-labelledby="meetly-interrupted-title"
    >
      <div className="flex gap-4 p-6">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700">
          <AlertTriangle size={19} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 id="meetly-interrupted-title" className="text-base font-semibold text-[var(--gt-text-primary)]">
            A recording was interrupted
          </h2>
          <p className="mt-2 text-sm leading-6 text-[var(--gt-text-secondary)]">
            About {formatDurationLabel(elapsed)} of audio was captured and sent for
            processing before this window closed. You can finish it now, or leave it.
          </p>
          {error && (
            <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold leading-5 text-red-800">
              {error}
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void finish()}
              disabled={busy !== null}
              className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white hover:bg-[var(--gt-primary-hover)] disabled:cursor-wait disabled:opacity-70"
            >
              {busy === 'finish' && <Loader2 className="animate-spin motion-reduce:animate-none" size={14} aria-hidden="true" />}
              {busy === 'finish' ? 'Finishing…' : 'Finish this meeting'}
            </button>
            <button
              type="button"
              onClick={discard}
              disabled={busy !== null}
              className="inline-flex min-h-10 items-center rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-2 text-sm font-semibold text-[var(--gt-text-secondary)] hover:bg-[var(--gt-surface-1)] disabled:opacity-60"
            >
              Discard it
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MeetlyInterruptedSession;
